import _ from 'lodash';
import { CheckoutTypes } from './constants';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import { isUsingCashier } from './CashierUtils';

/**
 * Enum for validatePaymentsAmount Result type.
 * @readonly
 * @enum {string}
 */
export const ValidationResultType = {
  UNDER_PAY: 'UNDER_PAY',
  OVER_PAY: 'OVER_PAY',
  EXACT: 'EXACT',
};

/**
 * @typedef {Object} Result
 * @property {ValidationResultType} type the result type
 * @property {number} diff
 *
 * @param {number} cartTotal
 * @param {Array<object>} payments
 *
 * @returns {Result}
 */
export function validatePaymentsAmount(cartTotal, payments) {
  const paymentTotal = _.reduce(payments, (memo, payment) => memo + (payment.amount || 0), 0);
  // we have a bug when splitting payment, that the all the payments dont add up to total, with 0.01 difference.so we let 0.01 difference still be valid
  if (paymentTotal + 1 < cartTotal) {
    return { type: ValidationResultType.UNDER_PAY, diff: cartTotal - paymentTotal };
  } else if (paymentTotal - 1 > cartTotal) {
    return { type: ValidationResultType.OVER_PAY, diff: paymentTotal - cartTotal };
  } else {
    return { type: ValidationResultType.EXACT };
  }
}

export class CustomerComment {
  constructor({ text, userId, timestamp }) {
    this.text = text;
    this.userId = userId;
    this.timestamp = timestamp || Date.now();
    return { ...this }; // todo masha smells fishy
  }

  toJson() {
    return { ...this };
  }
  // static fromJson(json) {
  //   const { text, userId, timestamp } = JSON.parse(json);
  //   return new CustomerComment({ text, userId, timestamp });
  // }
}

export function getPaymentsWithUpdatedAmount(payments, checkoutType) {
  return payments.map(payment => {
    payment.amount = getAmount(payment, checkoutType);
    if (payment.tip) {
      payment.amount = payment.amount + payment.tip;
    }
    return payment; // todo masha why sometimes amount is undefined
  });
}

function getAmount(payment, checkoutType) {
  return (function() {
    switch (checkoutType) {
      case CheckoutTypes.MANUALLY:
        return payment.manualAmount;
      case CheckoutTypes.PAY_PER_DISH:
        return payment.ppdAmount;
      default:
        return payment.amount;
    }
  })();
}

export function currencyToStringFactory({ currency, locale, symbol = true }) {
  return function formatter(amount) {
    return currencyToString(amount, currency, locale, symbol);
  };
}

export function currencyToString(amount, currency, locale, symbol = true) {
  const str = CurrencyUtils.toStr(amount / 100, currency, locale);
  const currencySymbol = CurrencyUtils.currencySymbol(currency);

  if (symbol) {
    return str;
  } else {
    return str
      .replace(/^[A-Z$]*/, '')
      .replace(currencySymbol, '')
      .trim();
  }
}

export function isOrganizationOfflineCreditCard(organization) {
  return _.get(organization, 'creditcardsInfo.collectionMethod') !== 'online';
}

export function getAllowedPaymentMethods({ organization, payments = [] } = {}) {
  const result = [];

  const paymentTypes = getSupportedPaymentTypes(organization);
  const currentPaymentTypes = payments.map(payment => payment.type);
  const isOfflineCreditCard = isOrganizationOfflineCreditCard(organization);

  if (paymentTypes.includes('cash') && !currentPaymentTypes.includes('cash')) {
    result.push('cash');
  }

  if (paymentTypes.includes('credit')) {
    // In case of offline payments, we only support a single card
    if (!isOfflineCreditCard || !currentPaymentTypes.includes('credit')) {
      result.push('credit');
    }
  }

  return result;
}

function getSupportedPaymentTypes(organization) {
  const organizationPaymentTypes = _.get(organization, 'paymentTypes', []);
  const clientSupportedPaymentTypes = ['cash', 'credit'];
  return _.intersection(organizationPaymentTypes, clientSupportedPaymentTypes);
}

export function isSplitPaymentAllowed({ organization, payments = [] } = {}) {
  const allowedPaymentMethods = getAllowedPaymentMethods({ organization, payments });
  const paymentTypes = getSupportedPaymentTypes(organization);
  const onlyCashSupported = paymentTypes.length === 1 && paymentTypes[0] === 'cash';
  const isCashierEnabled = isUsingCashier(organization);
  return (
    Boolean(organization) &&
    !organization.multiPaymentDisabled &&
    allowedPaymentMethods.length > 0 &&
    !onlyCashSupported &&
    !isCashierEnabled
  );
}

export function getPaymentsWithAmountGreaterThanZero(payments) {
  return payments.filter(payment => payment.amount > 0);
}

export function getCashierPaymentFromOrder(orderInfo) {
  const payments = _.get(orderInfo, 'payments');
  return _.find(payments, p => p.type === 'cashier');
}

export function isOrderChallenged(orderInfo = {}) {
  const status = _.get(orderInfo, 'status');
  const hasCashierPayment = Boolean(getCashierPaymentFromOrder(orderInfo));

  return status === 'pending' && hasCashierPayment;
}
